// src/utils/impersonation.ts
// Downward impersonation (GitLab-style): an Admin/SuperAdmin browses the whole
// app as a lower-role user. The swap happens server-side per request (the API
// validates the REAL token's role and replaces the principal), so the UI only
// keeps which subject to act as and sends it as a header; it never mints or
// stores another user's token. Session-scoped on purpose: impersonation is a
// per-tab support action and must not leak into other tabs or survive the
// browser closing.

import type { UserRole } from '@/features/users/types';

export const IMPERSONATION_HEADER = 'X-Impersonate-Subject';

const STORAGE_KEY = 'impersonation';

/** Mirrors Middlewares/ImpersonationMiddleware.cs RoleRank — higher number = higher privilege. */
const ROLE_RANK: Record<string, number> = {
  Viewer: 1,
  HotelAdmin: 2,
  GroupAdmin: 3,
  Admin: 4,
  SuperAdmin: 5,
};

/** Rank of a role string; unknown/legacy roles (e.g. 'Receptionist') rank 0, same as the API. */
const rankOf = (role: string | null | undefined): number => (role ? ROLE_RANK[role] ?? 0 : 0);

export interface ImpersonationState {
  /** Identity-provider subject (user id) being impersonated. */
  sub: string;
  /** What the app-bar pill shows (the target's name or email). */
  displayName: string;
  /** The target's role — becomes the effective role app-wide. */
  role: UserRole;
}

/** Minimal shape needed to evaluate the impersonation guard for a users-list row. */
export interface ImpersonationTarget {
  id: string;
  role: UserRole;
  isActive: boolean;
}

export const getImpersonation = (): ImpersonationState | null => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
};

export const startImpersonation = (state: ImpersonationState): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const stopImpersonation = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};

/**
 * Mirror of the API's guard (strictly downward, active, never self, never
 * nested — only Admin/SuperAdmin may impersonate) so the row action only
 * shows where the request would succeed.
 */
export const canImpersonate = (
  actorRole: string | null | undefined,
  actorSub: string | null | undefined,
  target: ImpersonationTarget
): boolean => {
  if (getImpersonation()) return false;
  if (rankOf(actorRole) < ROLE_RANK.Admin) return false;
  if (!target.role || !target.isActive) return false;
  if (target.id === actorSub) return false;
  return rankOf(actorRole) > rankOf(target.role);
};
